import type { MimeTypeExtended } from 'interfaces/app';
import {
	type ActorRefFrom,
	assertEvent,
	assign,
	enqueueActions,
	setup,
	type SnapshotFrom,
	stopChild,
} from 'xstate';
import {
	createUploadItemActors, type UploadItemAdapters,
} from './adapters';
import {
	type ExternalUploadProgress,
	externalUploadSessionMachine,
	getExternalUploadItemIds,
	type UploadBatchItemSnapshot,
	uploadBatchMachine,
	type UploadError,
	type UploadItemEvent,
	type UploadItemInput,
	uploadItemMachine,
	type UploadItemTerminationReason,
	webUploadQueueMachine,
} from './machines';
import {
	projectUploadBatchItemSnapshot,
	projectUploadItemSnapshot,
	resolveUploadPhotoModelId,
	type UploadItemSnapshot,
	type UploadModelProjection,
} from './projection';

export type UploadSessionTransport = 'web' | 'native';

export type UploadItemCreationMetadata = {
	type?: MimeTypeExtended;
	width?: number | null;
	height?: number | null;
	uploadStart?: number | null;
	uploadEnd?: number | null;
	url?: string | null;
	photoModelId?: string | number | null;
};

export type UploadSessionAdapters = UploadItemAdapters;

export type UploadSessionInput = {
	online: boolean;
	maxConcurrentWebUploads: number;
	adapters: UploadSessionAdapters;
};

type UploadItemActor = ActorRefFrom<typeof uploadItemMachine>;
type UploadBatchActor = ActorRefFrom<typeof uploadBatchMachine>;
type WebUploadQueueActor = ActorRefFrom<typeof webUploadQueueMachine>;
type ExternalUploadSessionActor = ActorRefFrom<typeof externalUploadSessionMachine>;

export type UploadSessionCompletedItem = UploadItemCreationMetadata & {
	uploadId: string;
	name: string;
	totalBytes: number;
	photoModelId: number | string | null;
	uploadUrl: string | null;
	retryCount: number;
};

type UploadItemLogic = ReturnType<typeof uploadItemMachine.provide>;

export type UploadSessionContext = {
	itemLogic: UploadItemLogic;
	itemActors: Record<string, UploadItemActor>;
	itemTransports: Record<string, UploadSessionTransport>;
	itemMetadata: Record<string, UploadItemCreationMetadata>;
	completedItems: Record<string, UploadSessionCompletedItem>;
	batchRef: UploadBatchActor;
	webQueueRef: WebUploadQueueActor;
	externalRef: ExternalUploadSessionActor;
};

export type UploadSessionAddItem = {
	input: UploadItemInput;
	transport: UploadSessionTransport;
	metadata?: UploadItemCreationMetadata;
};

export type UploadSessionEvent =
	| { type: 'session.item.add';
		item: UploadSessionAddItem }
	| { type: 'session.item.retry';
		uploadId: string;
		reset?: boolean }
	| { type: 'session.item.send';
		uploadId: string;
		event: UploadItemEvent }
	| { type: 'session.item.metadata.update';
		uploadId: string;
		patch: UploadItemCreationMetadata }
	| { type: 'session.reset' }
	| { type: 'session.network.online' }
	| { type: 'session.network.offline' }
	| { type: 'session.external.qr.requested' }
	| { type: 'session.external.qr.shown' }
	| { type: 'session.external.qr.failed' }
	| { type: 'session.external.qr.aborted' }
	| { type: 'session.external.progress.received';
		data: ExternalUploadProgress }
	| { type: 'session.external.editing.started' }
	| { type: 'session.external.reset' }
	| { type: 'session.internal.item.snapshot';
		uploadId: string;
		transport: UploadSessionTransport;
		snapshot: UploadItemSnapshot }
	| { type: 'session.internal.item.failed';
		uploadId: string;
		transport: UploadSessionTransport;
		error: UploadError }
	| { type: 'session.internal.item.processingFailed';
		uploadId: string;
		transport: UploadSessionTransport;
		error: UploadError }
	| { type: 'session.internal.item.queueSlotReleasable';
		uploadId: string;
		transport: UploadSessionTransport }
	| { type: 'session.internal.item.terminated';
		uploadId: string;
		transport: UploadSessionTransport;
		reason: UploadItemTerminationReason }
	| { type: 'session.internal.webQueue.activated';
		id: string }
	| { type: 'session.internal.external.shouldCloseQr' }
	| { type: 'session.internal.external.itemsAccepted';
		items: UploadBatchItemSnapshot[] }
	| { type: 'session.internal.external.itemSnapshot';
		item: UploadBatchItemSnapshot };

export type UploadSessionEmittedEvent =
	| { type: 'session.item.projection';
		uploadId: string;
		transport: UploadSessionTransport;
		projection: UploadModelProjection }
	| { type: 'session.item.failed';
		uploadId: string;
		transport: UploadSessionTransport;
		error: UploadError }
	| { type: 'session.item.processingFailed';
		uploadId: string;
		transport: UploadSessionTransport;
		error: UploadError }
	| { type: 'session.item.terminated';
		uploadId: string;
		transport: UploadSessionTransport;
		reason: UploadItemTerminationReason }
	| { type: 'session.external.qr.shouldClose' };

type SessionSelf = {
	send: (event: UploadSessionEvent) => void;
};

function shouldRemoveFromBatch(snapshot: UploadItemSnapshot): boolean {
	return (
		snapshot.matches('rejected')
		|| snapshot.matches('removed')
		|| snapshot.matches('cancelled')
	);
}

function subscribeUploadItem(
	uploadId: string,
	transport: UploadSessionTransport,
	child: UploadItemActor,
	self: SessionSelf,
): void {
	child.on(
		'item.failed',
		(emitted) => {
			self.send({
				type: 'session.internal.item.failed',
				uploadId,
				transport,
				error: emitted.error,
			});
		},
	);
	child.on(
		'item.processingFailed',
		(emitted) => {
			self.send({
				type: 'session.internal.item.processingFailed',
				uploadId,
				transport,
				error: emitted.error,
			});
		},
	);
	child.on(
		'item.queueSlotReleasable',
		() => {
			self.send({
				type: 'session.internal.item.queueSlotReleasable',
				uploadId,
				transport,
			});
		},
	);
	child.on(
		'item.terminated',
		(emitted) => {
			self.send({
				type: 'session.internal.item.terminated',
				uploadId,
				transport,
				reason: emitted.reason,
			});
		},
	);
	child.subscribe((snapshot) => {
		self.send({
			type: 'session.internal.item.snapshot',
			uploadId,
			transport,
			snapshot,
		});
	});
}

export const uploadSessionMachine = setup({
	types: {} as {
		context: UploadSessionContext;
		emitted: UploadSessionEmittedEvent;
		events: UploadSessionEvent;
		input: UploadSessionInput;
	},
	actors: {
		batchMachine: uploadBatchMachine,
		webQueueMachine: webUploadQueueMachine,
		externalMachine: externalUploadSessionMachine,
	},
	actions: {
		spawnItem: assign({
			itemActors: ({ context, event, self, spawn }) => {
				assertEvent(
					event,
					'session.item.add',
				);

				const { input, transport } = event.item;
				const uploadId = input.uploadId;

				if (context.itemActors[uploadId]) {
					return context.itemActors;
				}

				const child = spawn(
					context.itemLogic,
					{
						systemId: `upload:${uploadId}`,
						input,
					},
				);

				subscribeUploadItem(
					uploadId,
					transport,
					child,
					self,
				);

				context.batchRef.send({
					type: 'batch.files.accepted',
					items: [projectUploadBatchItemSnapshot(child.getSnapshot())],
				});

				if (transport === 'web') {
					context.webQueueRef.send({
						type: 'webQueue.upload.accepted',
						id: uploadId,
					});
				} else {
					child.send({
						type: 'upload.validate',
					});
				}

				return {
					...context.itemActors,
					[uploadId]: child,
				};
			},
			itemTransports: ({ context, event }) => {
				assertEvent(
					event,
					'session.item.add',
				);

				if (context.itemActors[event.item.input.uploadId]) {
					return context.itemTransports;
				}

				return {
					...context.itemTransports,
					[event.item.input.uploadId]: event.item.transport,
				};
			},
			itemMetadata: ({ context, event }) => {
				assertEvent(
					event,
					'session.item.add',
				);

				const { input, metadata } = event.item;

				if (context.itemMetadata[input.uploadId]) {
					return context.itemMetadata;
				}

				return {
					...context.itemMetadata,
					[input.uploadId]: metadata ?? {},
				};
			},
			completedItems: ({ context, event }) => {
				assertEvent(
					event,
					'session.item.add',
				);

				const { uploadId } = event.item.input;

				/*
				 * Re-adding a previously-saved uploadId spawns a fresh actor;
				 * keeping the stale completed entry would surface the same id
				 * in both the completed and active projections at once.
				 */
				if (context.itemActors[uploadId] || !context.completedItems[uploadId]) {
					return context.completedItems;
				}

				const {
					[uploadId]: ignoredStale,
					...remainingCompleted
				} = context.completedItems;

				return remainingCompleted;
			},
		}),
		updateItemMetadata: assign({
			itemMetadata: ({ context, event }) => {
				assertEvent(
					event,
					'session.item.metadata.update',
				);

				const current = context.itemMetadata[event.uploadId] ?? {};

				return {
					...context.itemMetadata,
					[event.uploadId]: {
						...current,
						...event.patch,
					},
				};
			},
		}),
		forwardEventToItem: ({ context, event }) => {
			assertEvent(
				event,
				'session.item.send',
			);

			const actor = context.itemActors[event.uploadId];

			actor?.send(event.event);
		},
		retryItem: ({ context, event }) => {
			assertEvent(
				event,
				'session.item.retry',
			);

			const actor = context.itemActors[event.uploadId];
			const transport = context.itemTransports[event.uploadId];

			if (!actor || !transport) {
				return;
			}

			if (event.reset) {
				/*
				 * User-initiated retry: clear the exhausted retry budget in-place
				 * (a self-transition on the failed state) so the count-guarded
				 * upload.retry below can fire again. Automatic retries omit reset
				 * and stay capped.
				 */
				actor.send({
					type: 'upload.retry.reset',
				});
			}

			/*
			 * Retry semantics are uniform across transports: an item that
			 * cannot accept upload.retry (exhausted budget, non-retryable
			 * error, wrong state) is a no-op. Web items previously
			 * re-enqueued unconditionally, churning a queue slot through
			 * activate-then-release for a retry that could never run.
			 */
			if (!actor.getSnapshot().can({
				type: 'upload.retry',
			})) {
				return;
			}

			if (transport === 'web') {
				context.webQueueRef.send({
					type: 'webQueue.upload.accepted',
					id: event.uploadId,
				});
				return;
			}

			actor.send({
				type: 'upload.retry',
			});
		},
		discardItem: enqueueActions(({ context, event, enqueue }) => {
			assertEvent(
				event,
				'session.internal.item.terminated',
			);

			const { uploadId, reason, transport } = event;
			const actor = context.itemActors[uploadId];

			if (!actor) {
				return;
			}

			const finalSnapshot = actor.getSnapshot();
			const removeFromBatch = (
				reason === 'rejected'
				|| reason === 'removed'
				|| reason === 'cancelled'
			);

			if (reason === 'saved') {
				const metadata = context.itemMetadata[uploadId] ?? {};
				const completed: UploadSessionCompletedItem = {
					...metadata,
					uploadId,
					name: finalSnapshot.context.name,
					totalBytes: finalSnapshot.context.totalBytes,
					photoModelId: finalSnapshot.context.remotePhotoId ?? finalSnapshot.context.localPhotoId,
					uploadUrl: finalSnapshot.context.uploadUrl,
					retryCount: finalSnapshot.context.retryCount,
				};

				enqueue.assign({
					completedItems: ({ context: ctx }) => ({
						...ctx.completedItems,
						[uploadId]: completed,
					}),
				});
			}

			if (removeFromBatch) {
				enqueue.sendTo(
					({ context: ctx }) => ctx.batchRef,
					{
						type: 'item.removed',
						id: uploadId,
					},
				);
			} else {
				enqueue.sendTo(
					({ context: ctx }) => ctx.batchRef,
					{
						type: 'item.snapshot',
						item: projectUploadBatchItemSnapshot(finalSnapshot),
					},
				);
			}

			if (transport === 'web') {
				enqueue.sendTo(
					({ context: ctx }) => ctx.webQueueRef,
					{
						type: 'webQueue.upload.removed',
						id: uploadId,
					},
				);
			}

			enqueue(stopChild(({ context: ctx }) => ctx.itemActors[uploadId]));

			enqueue.assign({
				itemActors: ({ context: ctx }) => {
					const {
						[uploadId]: ignoredActor,
						...remainingActors
					} = ctx.itemActors;

					return remainingActors;
				},
				itemTransports: ({ context: ctx }) => {
					const {
						[uploadId]: ignoredTransport,
						...remainingTransports
					} = ctx.itemTransports;

					return remainingTransports;
				},
				itemMetadata: ({ context: ctx }) => {
					const {
						[uploadId]: ignoredMetadata,
						...remainingMetadata
					} = ctx.itemMetadata;

					return remainingMetadata;
				},
			});

			enqueue.emit({
				type: 'session.item.terminated',
				uploadId,
				transport,
				reason,
			});
		}),
		routeItemSnapshot: enqueueActions(({ context, event, enqueue }) => {
			assertEvent(
				event,
				'session.internal.item.snapshot',
			);

			const { uploadId, transport, snapshot } = event;

			/*
			 * Snapshot routing races against discardItem: a NON-terminal
			 * snapshot queued before termination can be processed after the
			 * actor was stopped and removed. Routing it would resurrect the
			 * item in the batch (upsert has no existence check) as a phantom
			 * save-blocking entry. Terminal snapshots stay routable for
			 * untracked ids: the item's final snapshot is legitimately
			 * processed after discardItem (terminated emits ahead of the
			 * subscribe-driven snapshot) and its batch effects are idempotent.
			 */
			if (!context.itemActors[uploadId] && !snapshot.hasTag('terminal')) {
				return;
			}

			const projection = projectUploadItemSnapshot(snapshot);

			enqueue.emit({
				type: 'session.item.projection',
				uploadId,
				transport,
				projection,
			});

			if (shouldRemoveFromBatch(snapshot)) {
				enqueue.sendTo(
					({ context: ctx }) => ctx.batchRef,
					{
						type: 'item.removed',
						id: uploadId,
					},
				);
				return;
			}

			enqueue.sendTo(
				({ context: ctx }) => ctx.batchRef,
				{
					type: 'item.snapshot',
					item: projectUploadBatchItemSnapshot(snapshot),
				},
			);
		}),
		emitItemFailed: enqueueActions(({ event, enqueue }) => {
			assertEvent(
				event,
				'session.internal.item.failed',
			);

			enqueue.emit({
				type: 'session.item.failed',
				uploadId: event.uploadId,
				transport: event.transport,
				error: event.error,
			});
		}),
		emitItemProcessingFailed: enqueueActions(({ event, enqueue }) => {
			assertEvent(
				event,
				'session.internal.item.processingFailed',
			);

			enqueue.emit({
				type: 'session.item.processingFailed',
				uploadId: event.uploadId,
				transport: event.transport,
				error: event.error,
			});
		}),
		releaseWebQueueSlot: ({ context, event }) => {
			assertEvent(
				event,
				'session.internal.item.queueSlotReleasable',
			);

			if (event.transport !== 'web') {
				return;
			}

			context.webQueueRef.send({
				type: 'webQueue.upload.released',
				id: event.uploadId,
			});
		},
		activateWebItem: ({ context, event }) => {
			assertEvent(
				event,
				'session.internal.webQueue.activated',
			);

			const uploadId = event.id;
			const actor = context.itemActors[uploadId];

			if (!actor) {
				context.webQueueRef.send({
					type: 'webQueue.upload.removed',
					id: uploadId,
				});
				return;
			}

			const snapshot = actor.getSnapshot();

			if (snapshot.matches('selected')) {
				actor.send({
					type: 'upload.validate',
				});
				return;
			}

			if (
				(
					snapshot.matches('uploadFailed')
					|| snapshot.matches('processingFailed')
				)
				&& snapshot.can({
					type: 'upload.retry',
				})
			) {
				actor.send({
					type: 'upload.retry',
				});
				return;
			}

			context.webQueueRef.send({
				type: 'webQueue.upload.released',
				id: uploadId,
			});
		},
		/*
		 * Network events fan out to batch + webQueue + live item actors: the
		 * external child is the lone exception — it mirrors a session running
		 * on a peer device, whose connectivity is independent of ours.
		 * (Initial seeding differs by child for the same reason: webQueue
		 * takes online via input, batch via the send in the context factory,
		 * external nothing.) Item actors need the signal so an item whose
		 * upload or processing request is in flight can pause instead of
		 * orphaning the request and wedging the batch; only the active in-flight
		 * states (uploading/uploadOffline and processingPhoto/processingOffline)
		 * react, every other item state ignores it.
		 */
		forwardOnlineToChildren: ({ context }) => {
			context.batchRef.send({
				type: 'network.online',
			});
			context.webQueueRef.send({
				type: 'network.online',
			});
			Object.values(context.itemActors).forEach((actor) => {
				actor.send({
					type: 'network.online',
				});
			});
		},
		forwardOfflineToChildren: ({ context }) => {
			context.batchRef.send({
				type: 'network.offline',
			});
			context.webQueueRef.send({
				type: 'network.offline',
			});
			Object.values(context.itemActors).forEach((actor) => {
				actor.send({
					type: 'network.offline',
				});
			});
		},
		resetAllChildren: enqueueActions(({ context, enqueue }) => {
			Object.values(context.itemActors).forEach((actor) => {
				actor.send({
					type: 'upload.remove',
				});
			});

			context.batchRef.send({
				type: 'batch.reset',
			});
			context.webQueueRef.send({
				type: 'webQueue.reset',
			});
			context.externalRef.send({
				type: 'externalUpload.reset',
			});

			enqueue.assign({
				completedItems: () => ({}),
			});
		}),
		forwardToBatchAccepted: ({ context, event }) => {
			assertEvent(
				event,
				'session.internal.external.itemsAccepted',
			);

			context.batchRef.send({
				type: 'batch.files.accepted',
				items: event.items,
			});
		},
		forwardToBatchSnapshot: ({ context, event }) => {
			assertEvent(
				event,
				'session.internal.external.itemSnapshot',
			);

			context.batchRef.send({
				type: 'item.snapshot',
				item: event.item,
			});
		},
		emitQrShouldClose: enqueueActions(({ enqueue }) => {
			enqueue.emit({
				type: 'session.external.qr.shouldClose',
			});
		}),
		forwardExternalEvent: ({ context, event }) => {
			assertEvent(
				event,
				[
					'session.external.qr.requested',
					'session.external.qr.shown',
					'session.external.qr.failed',
					'session.external.qr.aborted',
					'session.external.progress.received',
					'session.external.editing.started',
					'session.external.reset',
				],
			);

			switch (event.type) {
				case 'session.external.qr.requested':
				case 'session.external.reset': {
					/*
					 * A fresh external session is starting. The external child's
					 * clearSession action will wipe its own context, but the batch
					 * still holds placeholder items keyed `external-upload-0...N`
					 * from any previous session. Without explicit cleanup these
					 * stale ids would persist across sessions and could block save
					 * gating until the next upload happened to write through them.
					 */
					const priorSubmittedCount = context.externalRef.getSnapshot().context.submittedCount;
					getExternalUploadItemIds(priorSubmittedCount).forEach((id) => {
						context.batchRef.send({
							type: 'item.removed',
							id,
						});
					});

					context.externalRef.send(
						event.type === 'session.external.qr.requested'
							? {
								type: 'externalUpload.qr.requested',
							}
							: {
								type: 'externalUpload.reset',
							},
					);
					return;
				}
				case 'session.external.qr.shown':
					context.externalRef.send({
						type: 'externalUpload.qr.shown',
					});
					return;
				case 'session.external.qr.failed':
					context.externalRef.send({
						type: 'externalUpload.qr.failed',
					});
					return;
				case 'session.external.qr.aborted':
					context.externalRef.send({
						type: 'externalUpload.qr.aborted',
					});
					return;
				case 'session.external.progress.received': {
					const externalSnapshot = context.externalRef.getSnapshot();
					const priorSubmittedCount = externalSnapshot.context.submittedCount;
					const submittedCountChanged = priorSubmittedCount !== event.data.submittedCount;
					const startsAfterTerminalSession = externalSnapshot.hasTag('terminal');

					if (priorSubmittedCount > 0 && (submittedCountChanged || startsAfterTerminalSession)) {
						getExternalUploadItemIds(priorSubmittedCount).forEach((id) => {
							context.batchRef.send({
								type: 'item.removed',
								id,
							});
						});
					}

					if (startsAfterTerminalSession) {
						context.externalRef.send({
							type: 'externalUpload.reset',
						});
					}

					context.externalRef.send({
						type: 'externalUpload.progress.received',
						data: event.data,
					});
					return;
				}
				case 'session.external.editing.started':
					context.externalRef.send({
						type: 'externalUpload.editing.started',
					});
					return;
				default: {
					/*
					 * Compile-time exhaustiveness: a new session.external.*
					 * event wired into on: but not handled here fails the
					 * build instead of silently no-oping.
					 */
					const unhandled: never = event;

					throw new Error(`Unhandled external event: ${JSON.stringify(unhandled)}`);
				}
			}
		},
	},
}).createMachine({
	id: 'uploadSession',
	context: ({ input, self, spawn }) => {
		const batchRef = spawn(
			'batchMachine',
			{
				id: 'batch',
			},
		);
		const webQueueRef = spawn(
			'webQueueMachine',
			{
				id: 'webQueue',
				input: {
					maxActive: input.maxConcurrentWebUploads,
					online: input.online,
				},
			},
		);
		const externalRef = spawn(
			'externalMachine',
			{
				id: 'external',
			},
		);

		const sessionSelf: SessionSelf = self;

		webQueueRef.on(
			'webQueue.upload.activated',
			(emitted) => {
				sessionSelf.send({
					type: 'session.internal.webQueue.activated',
					id: emitted.id,
				});
			},
		);
		externalRef.on(
			'externalUpload.qr.shouldClose',
			() => {
				sessionSelf.send({
					type: 'session.internal.external.shouldCloseQr',
				});
			},
		);
		externalRef.on(
			'externalUpload.batch.itemsAccepted',
			(emitted) => {
				sessionSelf.send({
					type: 'session.internal.external.itemsAccepted',
					items: emitted.items,
				});
			},
		);
		externalRef.on(
			'externalUpload.batch.itemSnapshot',
			(emitted) => {
				sessionSelf.send({
					type: 'session.internal.external.itemSnapshot',
					item: emitted.item,
				});
			},
		);

		batchRef.send({
			type: input.online ? 'network.online' : 'network.offline',
		});

		return {
			itemLogic: uploadItemMachine.provide({
				actors: createUploadItemActors(input.adapters),
			}),
			itemActors: {},
			itemTransports: {},
			itemMetadata: {},
			completedItems: {},
			batchRef,
			webQueueRef,
			externalRef,
		};
	},
	on: {
		'session.item.add': {
			actions: 'spawnItem',
		},
		'session.item.send': {
			actions: 'forwardEventToItem',
		},
		'session.item.retry': {
			actions: 'retryItem',
		},
		'session.item.metadata.update': {
			actions: 'updateItemMetadata',
		},
		'session.reset': {
			actions: 'resetAllChildren',
		},
		'session.network.online': {
			actions: 'forwardOnlineToChildren',
		},
		'session.network.offline': {
			actions: 'forwardOfflineToChildren',
		},
		'session.external.qr.requested': {
			actions: 'forwardExternalEvent',
		},
		'session.external.qr.shown': {
			actions: 'forwardExternalEvent',
		},
		'session.external.qr.failed': {
			actions: 'forwardExternalEvent',
		},
		'session.external.qr.aborted': {
			actions: 'forwardExternalEvent',
		},
		'session.external.progress.received': {
			actions: 'forwardExternalEvent',
		},
		'session.external.editing.started': {
			actions: 'forwardExternalEvent',
		},
		'session.external.reset': {
			actions: 'forwardExternalEvent',
		},
		'session.internal.item.snapshot': {
			actions: 'routeItemSnapshot',
		},
		'session.internal.item.failed': {
			actions: 'emitItemFailed',
		},
		'session.internal.item.processingFailed': {
			actions: 'emitItemProcessingFailed',
		},
		'session.internal.item.queueSlotReleasable': {
			actions: 'releaseWebQueueSlot',
		},
		'session.internal.item.terminated': {
			actions: 'discardItem',
		},
		'session.internal.webQueue.activated': {
			actions: 'activateWebItem',
		},
		'session.internal.external.shouldCloseQr': {
			actions: 'emitQrShouldClose',
		},
		'session.internal.external.itemsAccepted': {
			actions: 'forwardToBatchAccepted',
		},
		'session.internal.external.itemSnapshot': {
			actions: 'forwardToBatchSnapshot',
		},
	},
});

export type UploadSessionSnapshot = SnapshotFrom<typeof uploadSessionMachine>;

export type UploadSessionMachineActor = ActorRefFrom<typeof uploadSessionMachine>;

export function getUploadItemMetadata(
	snapshot: UploadSessionSnapshot,
	uploadId: string,
): UploadItemCreationMetadata | undefined {
	return snapshot.context.itemMetadata[uploadId];
}

export type UploadModuleProjection = {
	waiting: UploadModelProjection['model'][];
	uploading: UploadModelProjection['model'][];
	error: UploadModelProjection['model'][];
	completed: UploadModelProjection['model'][];
};

function buildCompletedModel(item: UploadSessionCompletedItem): UploadModelProjection['model'] {
	return {
		id: item.uploadId,
		name: item.name,
		totalBytes: item.totalBytes,
		errorCount: item.retryCount,
		photoModelId: item.photoModelId,
		url: item.uploadUrl ?? item.url ?? undefined,
		queue: 1,
		overall: 100,
		_type: item.type,
		width: item.width ?? undefined,
		height: item.height ?? undefined,
		uploadStart: item.uploadStart ?? null,
		uploadEnd: item.uploadEnd ?? null,
	};
}

function buildActiveModel(
	context: UploadSessionContext,
	uploadId: string,
	actor: UploadItemActor,
): { bucket: UploadModelProjection['bucket'];
	model: UploadModelProjection['model'] } {
	const snapshot = actor.getSnapshot();
	const baseProjection = projectUploadItemSnapshot(snapshot);
	const metadata = context.itemMetadata[uploadId] ?? {};
	const photoModelId = resolveUploadPhotoModelId(
		snapshot.context,
		metadata.photoModelId,
	);

	return {
		bucket: baseProjection.bucket,
		model: {
			...baseProjection.model,
			photoModelId,
			url: snapshot.context.uploadUrl ?? metadata.url ?? undefined,
			_type: metadata.type,
			width: metadata.width ?? undefined,
			height: metadata.height ?? undefined,
			uploadStart: metadata.uploadStart ?? null,
			uploadEnd: metadata.uploadEnd ?? null,
		},
	};
}

export function projectUploadModuleState(context: UploadSessionContext): UploadModuleProjection {
	const waiting: UploadModelProjection['model'][] = [];
	const uploading: UploadModelProjection['model'][] = [];
	const error: UploadModelProjection['model'][] = [];

	Object.entries(context.itemActors).forEach(([
		uploadId,
		actor,
	]) => {
		const { bucket, model } = buildActiveModel(
			context,
			uploadId,
			actor,
		);

		switch (bucket) {
			case 'waiting':
				waiting.push(model);
				break;
			case 'uploading':
				uploading.push(model);
				break;
			case 'error':
				error.push(model);
				break;
			default:
		}
	});

	const completed = Object.values(context.completedItems).map(buildCompletedModel);

	return {
		waiting,
		uploading,
		error,
		completed,
	};
}
